import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Clock, Target, TrendingUp, User, X } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db as defaultDb } from '../../config/firebase';
import { evalSubcollectionFor } from '../../lib/personIkrs';
import { formatSubmittedAt } from '../../lib/utils';

/**
 * Everything one person submitted for a period, read-only, in one place.
 *
 * One tab per individual key result plus a final tab for the satisfaction form,
 * so a person who filled three self-evaluations and the satisfaction form has
 * four readable forms behind a single button. Each KR tab carries both sides of
 * the conversation — the employee's self-evaluation and the manager's feedback —
 * which is also what the Next Steps stage needs to show.
 *
 * Read with getDocs, not onSnapshot: the summary page opens this on demand for a
 * finished evaluation and has no use for live updates, and it already spends one
 * read per employee on stage status.
 *
 * Presentation follows EmployeeSatisfactionModal's read-only side rather than
 * SelfEvaluationModal's, which renders answers as disabled textareas — wrong for
 * somebody who is only here to read.
 */

const RATING_LABELS = {
  exceeds: 'Exceeds',
  meets: 'Meets',
  'needs-improvement': 'Needs Improvement',
};

const RATING_STYLES = {
  exceeds: 'bg-green-100 text-green-800',
  meets: 'bg-blue-100 text-blue-800',
};

const SATISFACTION_TAB = '__satisfaction__';

/** A stored answer, or an explicit "nothing here" — never a blank gap. */
function Answer({ value }) {
  const text = (value || '').trim();
  return (
    <div className="px-4 py-3 rounded-lg bg-white border border-gray-100 text-gray-700 text-sm whitespace-pre-wrap min-h-[60px]">
      {text || <span className="italic text-gray-400">Not answered</span>}
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-100">
      <p className="text-sm font-medium text-gray-800 mb-3">{title}</p>
      {children}
    </div>
  );
}

function EmptyState({ icon, title, detail }) {
  return (
    <div className="text-center py-12 px-6">
      <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto">{icon}</div>
      <p className="mt-4 text-sm font-semibold text-gray-700">{title}</p>
      {detail && <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">{detail}</p>}
    </div>
  );
}

export default function EvaluationResponsesModal({
  isOpen, onClose, employee, fy, period, ikrs, db: dbProp,
}) {
  const dbInstance = dbProp || defaultDb;
  const [evaluations, setEvaluations] = useState({});
  const [cards, setCards] = useState({});
  const [npsScore, setNpsScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState(null);

  const employeeId = employee ? (employee.uid || employee.id) : null;
  const personIkrs = useMemo(() => ikrs || [], [ikrs]);

  useEffect(() => {
    if (!isOpen || !employeeId || !period) return;

    let cancelled = false;
    const subcollection = evalSubcollectionFor(fy);
    const base = `users/${employeeId}/${subcollection}/${period}`;

    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [ikrSnap, cardSnap] = await Promise.all([
          getDocs(collection(dbInstance, `${base}/ikrs`)),
          getDocs(collection(dbInstance, `${base}/cards`)),
        ]);
        if (cancelled) return;

        const nextEvaluations = {};
        ikrSnap.forEach(docSnap => { nextEvaluations[docSnap.id] = docSnap.data(); });

        const nextCards = {};
        let nps = null;
        cardSnap.forEach(docSnap => {
          const data = docSnap.data();
          if (docSnap.id === 'npsScore') nps = data.content;
          else nextCards[docSnap.id] = data.content;
        });

        setEvaluations(nextEvaluations);
        setCards(nextCards);
        setNpsScore(nps);
      } catch (error) {
        console.error('Could not load evaluation responses:', error);
        if (!cancelled) setLoadError('These responses could not be loaded. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isOpen, employeeId, period, fy, dbInstance]);

  /* Default to the first key result, or the satisfaction form when the person owns
     no key results at all — otherwise the modal would open on nothing. */
  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(personIkrs.length > 0 ? personIkrs[0].id : SATISFACTION_TAB);
  }, [isOpen, personIkrs]);

  if (!isOpen) return null;

  const activeIkr = personIkrs.find(ikr => ikr.id === activeTab) || null;
  const activeEvaluation = activeIkr ? evaluations[activeIkr.id] : null;
  const selfEvaluation = activeEvaluation?.selfEvaluation;
  const managerFeedback = activeEvaluation?.managerFeedback;
  const hasSelfEval = !!(selfEvaluation?.wentWell || selfEvaluation?.couldBeBetter || selfEvaluation?.nextFocus);
  const submittedAt = activeEvaluation?.selfEvaluationSubmittedAt;

  const satisfactionAnswered = (npsScore !== null && npsScore !== undefined ? 1 : 0)
    + ['employeeExperience', 'supportSuggestions', 'otherComments']
      .filter(key => (cards[key] || '').trim()).length;

  const tabClasses = (isActive) => `px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
    isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
  }`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <User size={20} className="text-indigo-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-gray-900 truncate">
                  {employee?.name || employee?.email || 'Employee'}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {fy === 'FY2627' ? 'FY 26-27' : 'FY 25-26'} · {period} ·{' '}
                  {personIkrs.length} {personIkrs.length === 1 ? 'key result' : 'key results'}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
              <X size={24} />
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1">
            {personIkrs.map((ikr, index) => {
              const filled = !!evaluations[ikr.id]?.selfEvaluation?.wentWell;
              return (
                <button
                  key={ikr.id}
                  type="button"
                  onClick={() => setActiveTab(ikr.id)}
                  title={ikr.krName || 'Untitled key result'}
                  className={tabClasses(activeTab === ikr.id)}
                >
                  {ikr.krCode ? `${ikr.krCode} · ` : `KR ${index + 1} · `}
                  <span className="font-normal">
                    {(ikr.krName || 'Untitled').slice(0, 28)}{(ikr.krName || '').length > 28 ? '…' : ''}
                  </span>
                  {!loading && !filled && <span className="ml-1.5 opacity-60">○</span>}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setActiveTab(SATISFACTION_TAB)}
              className={tabClasses(activeTab === SATISFACTION_TAB)}
            >
              <ClipboardList size={12} className="inline mr-1.5 -mt-0.5" />
              Satisfaction form
              {!loading && <span className="ml-1.5 opacity-70">{satisfactionAnswered}/4</span>}
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-16 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto" />
              <p className="text-gray-500 mt-3 text-sm">Loading responses…</p>
            </div>
          ) : loadError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{loadError}</div>
          ) : activeTab === SATISFACTION_TAB ? (
            <div className="space-y-5">
              <Block title="1. On a scale of 1 to 10, how likely are you to recommend Rusk as a workplace to family and friends?">
                {npsScore !== null && npsScore !== undefined ? (
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 text-blue-700 font-bold text-lg">
                      {npsScore}
                    </span>
                    <span className="text-sm text-gray-500">1 = Not likely · 10 = Extremely likely</span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-400 italic">Not answered</span>
                )}
              </Block>
              <Block title="2. What is one thing we could change to make your employee experience better?">
                <Answer value={cards.employeeExperience} />
              </Block>
              <Block title="3. Any suggestion for support teams like Admin, HR, Finance & IT?">
                <Answer value={cards.supportSuggestions} />
              </Block>
              <Block title="4. Any other comments.">
                <Answer value={cards.otherComments} />
              </Block>
            </div>
          ) : !activeIkr ? (
            <EmptyState
              icon={<Target size={28} className="text-gray-400" />}
              title="No key results assigned"
              detail={`${employee?.name || 'This person'} owns no ${fy === 'FY2627' ? 'FY 26-27' : 'FY 25-26'} individual key results, so there is no self-evaluation to read. The satisfaction form tab may still have answers.`}
            />
          ) : (
            <div className="space-y-5">
              <div className="p-4 rounded-xl border border-gray-100 bg-white">
                <div className="flex items-start gap-1.5 flex-wrap">
                  {activeIkr.objectiveCode && (
                    <span className="text-[10px] font-mono font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">
                      {activeIkr.objectiveCode}
                    </span>
                  )}
                  {activeIkr.krCode && (
                    <span className="text-[10px] font-mono font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      {activeIkr.krCode}
                    </span>
                  )}
                  <span className="text-xs text-gray-600 leading-snug">
                    {activeIkr.objectiveText || activeIkr.objectiveName}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mt-2">
                  {activeIkr.krName || <span className="italic text-gray-400">Untitled key result</span>}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Target: {activeIkr.targetKpi ?? '—'} {activeIkr.kpiUnit}
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <h3 className="text-sm font-semibold text-gray-900">Self-evaluation</h3>
                  {hasSelfEval && (
                    <span className="text-xs text-gray-500 flex items-center gap-1.5">
                      <Clock size={12} className="text-gray-400" />
                      {submittedAt ? `Submitted ${formatSubmittedAt(submittedAt)}` : formatSubmittedAt(null)}
                    </span>
                  )}
                </div>
                {hasSelfEval ? (
                  <div className="space-y-4">
                    <Block title="🌟 What do you think went well since your last feedback talk?">
                      <Answer value={selfEvaluation?.wentWell} />
                    </Block>
                    <Block title="🔄 What could have gone better?">
                      <Answer value={selfEvaluation?.couldBeBetter} />
                    </Block>
                    <Block title="🎯 What should be your focus for the next period and what are the challenges to achieve them?">
                      <Answer value={selfEvaluation?.nextFocus} />
                    </Block>
                  </div>
                ) : (
                  <p className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 text-sm text-gray-400 italic">
                    Not submitted
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <h3 className="text-sm font-semibold text-gray-900">Manager feedback</h3>
                  {managerFeedback?.rating && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      RATING_STYLES[managerFeedback.rating] || 'bg-red-100 text-red-800'
                    }`}>
                      {RATING_LABELS[managerFeedback.rating] || managerFeedback.rating}
                    </span>
                  )}
                </div>
                {managerFeedback?.feedback ? (
                  <Block title={managerFeedback.managerName ? `Feedback from ${managerFeedback.managerName}` : 'Feedback'}>
                    <Answer value={managerFeedback.feedback} />
                  </Block>
                ) : (
                  <p className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 text-sm text-gray-400 italic">
                    Not submitted
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-between gap-3 flex-shrink-0">
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            <TrendingUp size={12} />
            Read-only. Edit on the Evaluations tab.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
